import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { SubpageHero } from '../components/SubpageHero';
import { COMPANY } from '../data/company';

export function AboutPage() {
  return (
    <>
      <SEO 
        title="Om Altmuligmanden" 
        description="Læs om Altmuligmanden Juelsminde – din lokale håndværker med fokus på kvalitet, nærvær, grundighed og god dialog." 
      />

      {/* Header Banner */}
      <SubpageHero
        badge="Lokalt Håndværk i Juelsminde"
        title="Om Altmuligmanden Juelsminde"
        subtitle="Kvalitetsarbejde med fokus på tillid, aftaler der overholdes og god personlig betjening."
        breadcrumbs={[
          { label: "Om Altmuligmanden" }
        ]}
      />

      {/* Main About Content */}
      <section className="py-20 bg-sand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Image Box */}
            <div className="lg:col-span-5 space-y-4">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/3afae24a-c90c-4b91-94fe-484783460244/img-1851.jpeg"
                  alt="Jonas Vilhelmsen - Altmuligmanden Juelsminde"
                  className="w-full h-auto object-cover aspect-4/5"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-forest p-6 text-white">
                  <h3 className="font-bold text-lg text-white">Jonas Vilhelmsen</h3>
                  <p className="text-xs text-sand/80">Indehaver & Altmuligmand</p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-sand-border shadow-xs text-sm space-y-2">
                <div className="flex justify-between border-b border-sand-border pb-2">
                  <span className="text-muted font-medium">Firmanavn:</span>
                  <span className="font-bold text-forest">{COMPANY.name}</span>
                </div>
                <div className="flex justify-between border-b border-sand-border pb-2">
                  <span className="text-muted font-medium">CVR-nummer:</span>
                  <span className="font-bold text-forest whitespace-nowrap">{COMPANY.cvr}</span>
                </div>
                <div className="flex justify-between border-b border-sand-border pb-2">
                  <span className="text-muted font-medium">Lokalitet:</span>
                  <span className="font-bold text-forest">{COMPANY.city}</span>
                </div>
              </div>
            </div>

            {/* Content Box */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-copper font-bold text-xs uppercase tracking-widest bg-copper-light px-3.5 py-1 rounded-full">
                Din Lokale Partner
              </span>

              <h2 className="text-2xl sm:text-4xl font-extrabold text-forest leading-snug">
                Pålidelig og alsidig hjælp til dit hjem og din have
              </h2>

              <div className="prose prose-lg text-charcoal space-y-4 leading-relaxed">
                <p className="text-lg font-medium text-forest/90">
                  Velkommen til din lokale altmuligmand – din pålidelige hjælp til både små og store opgaver i hjemmet. Jeg tilbyder professionel og effektiv service med fokus på kvalitet, grundighed og god dialog. Uanset om det gælder reparationer, montering, vedligeholdelse eller praktisk hjælp inde og ude, står jeg klar til at gøre hverdagen nemmere for dig. 🛠️
                </p>

                <p className="text-muted text-base">
                  Mange husstande i Juelsminde og omegn har opgaver, der hober sig op – en vandhane der drypper, nye møbler der skal samles, en hæk der trænger til klipning eller loftet der skal ryddes. For mange er tiden eller kræfterne knap. Det er her, jeg træder til med de rette værktøjer, erfaring og godt humør.
                </p>

                <p className="text-muted text-base">
                  Hos Altmuligmanden Juelsminde er ingen opgave for lille. Jeg sætter en ære i at møde op som aftalt, udføre arbejdet ordentligt og efterlade arbejdsområdet pænt og ryddeligt.
                </p>
              </div>

              {/* Value Props Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="bg-white p-5 rounded-xl border border-sand-border flex items-start gap-4 shadow-2xs">
                  <div className="w-10 h-10 bg-forest text-white rounded-lg flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-square-check" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-forest text-sm">Grundighed & Kvalitet</h4>
                    <p className="text-xs text-muted mt-0.5">Professionel udførelse med øje for detaljen.</p>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-sand-border flex items-start gap-4 shadow-2xs">
                  <div className="w-10 h-10 bg-forest text-white rounded-lg flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-comments" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-forest text-sm">God & Åben Dialog</h4>
                    <p className="text-xs text-muted mt-0.5">Tydelige aftaler og klare priser.</p>
                  </div>
                </div>
              </div>

              {/* Call to action */}
              <div className="pt-6 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="bg-copper hover:bg-copper-hover text-white font-bold px-8 py-3.5 rounded-xl shadow-md transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  <span>Få et uforpligtende tilbud</span>
                </Link>

                <a
                  href={`tel:${COMPANY.phone}`}
                  className="bg-forest text-white font-bold px-6 py-3.5 rounded-xl hover:bg-forest-dark transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring: {COMPANY.phoneFormatted}</span>
                </a>
              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
